import { z } from "zod";
import { isKlipyCdnUrl } from "@/lib/klipy-cdn";
import {
  type ExternalMediaAsset,
  MAX_TAGS_PER_IMAGE,
  type MediaAssetSlim,
} from "@/lib/media";
import { isSafeMediaKey } from "@/lib/security/user-paths";

const mediaTagSchema = z
  .object({
    userId: z.string().min(1, "User id is required"),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
  })
  .strict();

export const mediaAssetSlimSchema: z.ZodType<MediaAssetSlim, MediaAssetSlim> = z
  .object({
    id: z.string().min(1, "Media id is required"),
    key: z
      .string()
      .min(1, "Media key is required")
      .refine(isSafeMediaKey, "Media key is invalid"),
    order: z
      .number({ message: "Order is required" })
      .int("Order must be an integer")
      .min(0, "Order must be 0 or greater"),
    posterKey: z.string().min(1).refine(isSafeMediaKey).optional(),
    hlsKey: z.string().min(1).refine(isSafeMediaKey).optional(),
    variants: z
      .array(
        z
          .object({
            key: z.string().refine(isSafeMediaKey),
            width: z.number().int().positive().max(16384),
            height: z.number().int().positive().max(16384),
          })
          .strict(),
      )
      .max(8)
      .optional(),
    width: z.number().int().min(1).max(16_384).optional(),
    height: z.number().int().min(1).max(16_384).optional(),
    blurHash: z.string().min(1).max(100).optional(),
    tags: z.array(mediaTagSchema).max(MAX_TAGS_PER_IMAGE).optional(),
  })
  .strict() satisfies z.ZodType<MediaAssetSlim>;

export const externalMediaAssetSchema: z.ZodType<
  ExternalMediaAsset,
  ExternalMediaAsset
> = z
  .object({
    id: z.string().min(1, "Media id is required"),
    key: z.string().min(1, "Media key is required"),
    order: z
      .number({ message: "Order is required" })
      .int("Order must be an integer")
      .min(0, "Order must be 0 or greater"),
    provider: z.literal("klipy"),
    url: z
      .string()
      .url("Media URL is invalid")
      .refine(isKlipyCdnUrl, "Media must be hosted on the KLIPY CDN"),
    kind: z.enum(["image", "video"]),
    mimeType: z.string().min(1).optional(),
    title: z.string().trim().max(120).nullable().optional(),
  })
  .strict();

export const mediaAssetArraySchema = z
  .array(mediaAssetSlimSchema)
  .superRefine((items, ctx) => {
    const seenOrder = new Set<number>();
    const seenIds = new Set<string>();

    for (const item of items) {
      if (seenOrder.has(item.order)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Each media item must have a unique order",
        });
        return;
      }

      if (seenIds.has(item.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Each media item must have a unique id",
        });
        return;
      }

      seenOrder.add(item.order);
      seenIds.add(item.id);
    }
  });
